/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useEffect, useState, useCallback } from 'react'
import { t } from '@sportninja/common/i18n'
import { getAllOfficialTypes } from '@sportninja/common/selectors/types'
import { connect } from 'react-redux'
import colors from '@sportninja/common/constants/appColors'
import { EditOutlined } from '@ant-design/icons'

import NewButton from '../Page/NewButton'
import { InviteAction } from '../ActionPanel/ActionPanelActions'
import useGetListItems from './useGetListItems'
import OptionButtonDelete from './OptionButtons/Delete'
import List from '.'
import PersonCell from './PersonCell'
import MoreOptions from './OptionButtons/More'
import { Empty } from 'antd'
import { formatPhoneNumber } from 'react-phone-number-input'
import OfficialModal from '../OfficialModal'

export const AddOfficial = ({
  permission,
  entityType,
  entityId,
  onComplete,
  isSoccer = false,
  isOrg = false,
  sportId = null,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleSuccess = useCallback(() => {
    onComplete()
  }, [onComplete])

  if (!permission.admin) {
    return null
  }

  return (
    <>
      <NewButton onClick={handleOpenModal} />
      <OfficialModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        entityType={entityType}
        entityId={entityId}
        onSuccess={handleSuccess}
        isSoccer={isSoccer}
        isOrg={isOrg}
        sportId={sportId}
      />
    </>
  )
}

const OfficialList = ({
  entityType,
  entityId,
  slug,
  refreshKey,
  isSoccer = false,
  isOrg = false,
  sportId,
  officialTypes,
}) => {
  const listProps = useGetListItems(slug)
  const { sort, direction, getItems, items } = listProps

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedOfficial, setSelectedOfficial] = useState(null)

  const handleEditClick = useCallback((item) => {
    setSelectedOfficial(item)
    setIsModalOpen(true)
  }, [])

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
    setSelectedOfficial(null)
  }, [])

  const handleModalSuccess = useCallback(() => {
    getItems()
  }, [getItems])

  useEffect(() => {
    getItems()
  }, [refreshKey, sort, direction])

  const headers = [
    {
      text: t('common:name'),
      sizePercent: 34,
      sort: 'name_first',
      Cell: PersonCell,
    },
    {
      text: t('common:role'),
      sizePercent: 19,
      minWidth: 100,
      sort: 'official_type_id',
      accessor: (item) => {
        return officialTypes.find((t) => t.id === item.official_type)?.name_full
      },
    },
    {
      text: t('common:email'),
      sizePercent: 34,
      minWidth: 240,
      sort: 'email',
      accessor: 'email',
    },
    {
      text: t('common:phone'),
      sizePercent: 34,
      minWidth: 240,
      sort: 'phone_number',
      accessor: 'phone_number',
      Cell: ({ item }) => {
        return item?.phone_number ? formatPhoneNumber(item?.phone_number) : ''
      },
    },
    {
      text: 'Invite Status',
      sizePercent: 13,
      sort: 'is_accepted',
      minWidth: 80,
      accessor: (item) => {
        let status = '-'
        if (item?.is_invited) {
          status = 'Invited'
        }
        if (item?.is_accepted) {
          status = 'Accepted'
        }

        return status
      },
    },
  ]

  const options = (item) => {
    const itemSlug = `/${entityType}/${entityId}/officials/${item.id}`

    const opts = [
      <button
        key='edit'
        type='button'
        onClick={() => handleEditClick(item)}
        css={css`
          background: transparent;
          border: none;
          cursor: pointer;
          color: ${colors.PRIMARY};
          padding: 4px 8px;
          font-size: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          &:hover {
            opacity: 0.8;
          }
        `}
        title={t('Web:editOfficial')}
      >
        <EditOutlined />
      </button>,
      <InviteAction
        key='invite'
        invite={{
          is_invited: item.is_invited,
          is_accepted: item.is_accepted,
          invitation_id: item.invitation_id,
        }}
        user={{
          id: item.id,
          name_first: item.name_first,
          name_last: item.name_last,
          official_type_id: item.official_type,
          email: item?.email,
        }}
        slug={itemSlug}
        onComplete={getItems}
      />,
      <MoreOptions
        key='more'
        options={[
          {
            key: 'remove',
            Component: OptionButtonDelete,
            componentProps: {
              buttonText: 'remove',
              slug: itemSlug,
              iconName: 'user-minus',
              title: `Remove ${item.full_name}`,
              type: t('common:official'),
              itemId: item.id,
              onComplete: getItems,
            },
            title: 'remove',
          },
        ]}
      />,
    ]

    return opts.filter((o) => o !== undefined)
  }

  return (
    <>
      {items.length > 0 ? (
        <List
          headers={headers}
          options={options}
          listProps={listProps}
          noItemsText={t('common:official_plural', { count: 2 })}
        />
      ) : (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 500px;
          `}
        >
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}
      <OfficialModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        initialData={selectedOfficial}
        entityType={entityType}
        entityId={entityId}
        onSuccess={handleModalSuccess}
        isSoccer={isSoccer}
        isOrg={isOrg}
        sportId={sportId}
      />
    </>
  )
}

const mapStateToProps = (state, { sportId }) => {
  return {
    officialTypes: getAllOfficialTypes(state),
  }
}

export default connect(mapStateToProps)(OfficialList)
